// NUCLEAR APPROACH: Centralized Auth Token Manager
// This eliminates ALL hook violations by managing tokens outside of React components

class AuthTokenManager {
  constructor() {
    this.token = null;
    this.getAccessTokenSilently = null;
    this.listeners = new Set();
    this.isGettingToken = false;
  }

  // Initialize with Auth0's getAccessTokenSilently function
  init(getAccessTokenSilentlyFn) {
    this.getAccessTokenSilently = getAccessTokenSilentlyFn;
  }

  // Subscribe to token changes
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Notify all listeners of token change
  notifyListeners() {
    this.listeners.forEach(listener => listener(this.token));
  }

  // Get token - this is the ONLY place where getAccessTokenSilently is called
  async getToken(user) {
    // Early return if no function, no user, or already getting token
    if (!this.getAccessTokenSilently || !user) {
      if (!user && this.token) {
        // User logged out, clear token
        this.clearToken();
      }
      return null;
    }

    // Prevent multiple simultaneous token requests
    if (this.isGettingToken) {
      return this.token;
    }

    try {
      this.isGettingToken = true;
      console.log('🔐 [AuthTokenManager] Getting Auth0 token...');
      
      const newToken = await this.getAccessTokenSilently();
      
      if (newToken !== this.token) {
        this.token = newToken;
        this.notifyListeners();
        console.log('✅ [AuthTokenManager] Token updated');
      }
      
      return this.token;
    } catch (error) {
      console.error('❌ [AuthTokenManager] Error getting token:', error);
      // Don't clear token on error if user is still authenticated
      // Only clear if it's an authentication error
      if (error.error === 'login_required' || error.error === 'access_denied') {
        this.token = null;
        this.notifyListeners();
      }
      return null;
    } finally {
      this.isGettingToken = false;
    }
  }

  // Clear token (on logout)
  clearToken() {
    console.log('🔐 [AuthTokenManager] Clearing token on logout');
    this.token = null;
    this.isGettingToken = false; // Reset getting token flag
    this.notifyListeners();
  }

  // Cleanup on logout (remove references to avoid memory leaks)
  cleanup() {
    console.log('🔐 [AuthTokenManager] Cleaning up on logout');
    this.token = null;
    this.isGettingToken = false;
    this.getAccessTokenSilently = null;
    this.notifyListeners();
  }

  // Get current token without making a request
  getCurrentToken() {
    return this.token;
  }
}

// Export singleton instance
export const authTokenManager = new AuthTokenManager();

// React hook that uses the manager (NO HOOK VIOLATIONS)
import { useState, useEffect } from 'react';

export function useAuthToken(user, getAccessTokenSilently) {
  const [token, setToken] = useState(authTokenManager.getCurrentToken());

  useEffect(() => {
    // Initialize the manager
    if (getAccessTokenSilently) {
      authTokenManager.init(getAccessTokenSilently);
    }

    // Subscribe to token changes
    const unsubscribe = authTokenManager.subscribe(setToken);

    // Request token if we have a user, otherwise clear it
    if (user && getAccessTokenSilently) {
      authTokenManager.getToken(user);
    } else {
      authTokenManager.clearToken();
    }

    return unsubscribe;
  }, [user, getAccessTokenSilently]);

  // Clear token when component unmounts or user changes to null
  useEffect(() => {
    return () => {
      if (!user) {
        authTokenManager.clearToken();
      }
    };
  }, [user]);

  return token;
} 